import * as React from "react"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"

import { APARENCIA_DE_CAMPO } from "../lib/classes"
import { cn } from "../lib/cn"
import { Badge } from "./Badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./Command"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"

export interface Option {
  value: string | number
  label: string
  subLabel?: string
  busca?: string
}

interface InputSelectComboboxBaseProps {
  options: Option[]
  placeholder?: string
  title?: string
  loading?: boolean
  disabled?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  id?: string
  required?: boolean
  placeholderDaBusca?: string
  className?: string
  "aria-label"?: string
}

export type InputSelectComboboxProps = InputSelectComboboxBaseProps &
  (
    | {
        multiple: true
        value: Option["value"][]
        onChange: (value: Option["value"][]) => void
      }
    | {
        multiple?: false
        value: Option["value"] | null
        onChange: (value: Option["value"] | null) => void
      }
  )

function InputSelectCombobox(props: InputSelectComboboxProps) {
  const {
    options,
    placeholder,
    title,
    loading,
    disabled,
    open: openControlado,
    onOpenChange,
    id,
    required,
    placeholderDaBusca,
    className,
    "aria-label": rotulo,
  } = props
  const [openInterno, setOpenInterno] = React.useState(false)
  const open = openControlado ?? openInterno
  const setOpen = onOpenChange ?? setOpenInterno

  const gatilhoRef = React.useRef<HTMLButtonElement>(null)
  const conteudoRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const aoClicarFora = (evento: PointerEvent) => {
      const alvo = evento.target as Node
      if (gatilhoRef.current?.contains(alvo)) return
      if (conteudoRef.current?.contains(alvo)) return
      setOpen(false)
    }
    document.addEventListener("pointerdown", aoClicarFora, true)
    return () => document.removeEventListener("pointerdown", aoClicarFora, true)
  }, [open, setOpen])

  const selectedValues = props.multiple ? props.value : props.value !== null ? [props.value] : []

  const selectedOptions = options.filter((option) => selectedValues.includes(option.value))

  const handleSelect = (optionValue: Option["value"]) => {
    if (props.multiple) {
      const exists = props.value.includes(optionValue)
      props.onChange(exists ? props.value.filter((v) => v !== optionValue) : [...props.value, optionValue])
      return
    }

    props.onChange(optionValue === props.value ? null : optionValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={gatilhoRef}
          id={id}
          type="button"
          aria-required={required ? true : undefined}
          aria-label={rotulo}
          title={title}
          disabled={disabled}
          data-slot="input-select-combobox-trigger"
          className={cn(
            props.multiple ? "h-auto min-h-9 py-1.5" : "h-9 py-1",
            "flex w-full min-w-35 items-center justify-between gap-2 border border-input px-3 text-sm transition-colors outline-none",
            APARENCIA_DE_CAMPO,
            "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <div className="flex min-w-0 flex-1 flex-wrap gap-1 text-left">
            {!props.multiple && selectedOptions[0] !== undefined ? (
              <span className="truncate">{selectedOptions[0].label}</span>
            ) : selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge key={option.value} variant="secondary">
                  {option.label}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder ?? "Selecione..."}</span>
            )}
          </div>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent ref={conteudoRef} align="start" className="w-72 min-w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder={placeholderDaBusca ?? "Buscar..."} />
          <CommandList>
            {loading ? (
              <div data-slot="input-select-combobox-loading" className="py-6 text-center text-sm text-muted-foreground">
                Carregando...
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhum resultado.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value)
                    return (
                      <CommandItem
                        key={option.value}
                        value={[option.label, option.subLabel, option.busca].filter(Boolean).join(" ")}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <CheckIcon className={cn("size-4", isSelected ? "opacity-100" : "opacity-0")} />
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          {option.subLabel && <span className="text-xs text-muted-foreground">{option.subLabel}</span>}
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { InputSelectCombobox }
